import React, { useEffect, useState } from "react";
import { api, getToken } from "@/lib/api";
import Shell from "@/components/Shell";
import { Loader2, Search, Trash2, AlertTriangle, RefreshCcw, Download, Upload } from "lucide-react";
import { DOMAIN_META, LIFE_KEYS, OPS_KEYS } from "@/lib/domains";
import { lunarMeta, circadianMeta, browserTzOffsetHours, LUNAR_META, CIRCADIAN_META } from "@/lib/overlay";
import EasterEggSlot from "@/components/EasterEggSlot";
import CleanupQueuePanel from "@/components/CleanupQueuePanel";  // iter 31.219/220

// Iter 31.19 (23 May 2026) — dramatic self-destruct countdown for the
// ledger wipe + GDPR-style data export. Per No. 6: "the wipe is too easy
// and needs a countdown timer like a self-destruct sequence, it needs
// drama, equally id like it accompanied by a download my data button".
// Iter 31.31 (Feb 2026) — No. 6 reported the Ledger getting unwieldy on
// resume: 1k+ entries dumped onto the page in one go. Switched to paged
// loads (LEDGER_PAGE_SIZE per slice) appended onto an infinite-scroll
// timeline. The backend already supports `?offset` + `has_more`.
const LEDGER_PAGE_SIZE = 100;
const WIPE_COUNTDOWN_SECONDS = 10;

export default function Ledger() {
    const [entries, setEntries] = useState(null);
    const [counts, setCounts] = useState({});
    const [totalCountDocs, setTotalCountDocs] = useState(null);
    const [domain, setDomain] = useState("");
    // Iter 31.45 (26 May 2026) — default the Ledger to TODAY so a 60k-row
    // production ledger doesn't kill the mobile browser on first paint.
    // No. 6's machine was choking on the full timeline. Pills below
    // toggle between today / week / month / all.
    const [since, setSince] = useState("today");
    const [q, setQ] = useState("");
    const [expanded, setExpanded] = useState({});
    const [showDanger, setShowDanger] = useState(false);
    const [dangerConfirmText, setDangerConfirmText] = useState("");
    const [wiping, setWiping] = useState(false);
    const [wipeError, setWipeError] = useState("");
    const [wipeCountdown, setWipeCountdown] = useState(null); // null | seconds remaining | 0=firing
    const [reshredding, setReshredding] = useState({}); // {entryId: bool}
    const [reshredError, setReshredError] = useState({}); // {entryId: msg}
    const [overlayMap, setOverlayMap] = useState({}); // entryId -> {lunar, circadian}
    const [overlayCounts, setOverlayCounts] = useState({ by_lunar: {}, by_circadian: {}, total: 0 });
    const [showOverlay, setShowOverlay] = useState(true);
    const [exportManifest, setExportManifest] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [importing, setImporting] = useState(false);
    const [importReport, setImportReport] = useState(null);
    const importInputRef = React.useRef(null);

    const [hasMore, setHasMore] = useState(false);
    const [nextOffset, setNextOffset] = useState(0);
    const [loadingMore, setLoadingMore] = useState(false);

    // Iter 31.205 — canon-safe bulk invalidate.
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [invalidating, setInvalidating] = useState(false);
    const [showInvalidateModal, setShowInvalidateModal] = useState(false);
    const [invalidateMode, setInvalidateMode] = useState("selected"); // "selected" | "filter"
    const [invalidateReason, setInvalidateReason] = useState("");
    const [filterMatchCount, setFilterMatchCount] = useState(null);
    const [invalidateResult, setInvalidateResult] = useState(null);

    const toggleSelected = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const selectAllVisible = () => setSelectedIds(new Set(entries.map((e) => e.id)));
    const clearSelection = () => setSelectedIds(new Set());

    const openInvalidateSelected = () => {
        if (selectedIds.size === 0) return;
        setInvalidateMode("selected");
        setInvalidateReason("");
        setInvalidateResult(null);
        setShowInvalidateModal(true);
    };
    const openInvalidateByFilter = async () => {
        setInvalidateMode("filter");
        setInvalidateReason("");
        setInvalidateResult(null);
        setFilterMatchCount(null);
        setShowInvalidateModal(true);
        // Dry-run preview.
        try {
            const body = { reason: "preview", dry_run: true };
            if (domain) body.domain = domain;
            if (q) body.q = q;
            if (since && since !== "all") body.since = since;
            const { data } = await api.post("/shed/ledger/invalidate-by-filter", body);
            setFilterMatchCount(data.would_invalidate || 0);
        } catch (err) {
            setFilterMatchCount(-1);
        }
    };
    const executeInvalidate = async () => {
        if (!invalidateReason.trim() || invalidateReason.trim().length < 2) return;
        setInvalidating(true);
        try {
            let res;
            if (invalidateMode === "selected") {
                res = await api.post("/shed/ledger/invalidate", {
                    entry_ids: Array.from(selectedIds),
                    reason: invalidateReason.trim(),
                });
            } else {
                const body = { reason: invalidateReason.trim() };
                if (domain) body.domain = domain;
                if (q) body.q = q;
                if (since && since !== "all") body.since = since;
                res = await api.post("/shed/ledger/invalidate-by-filter", body);
            }
            setInvalidateResult(res.data);
            setSelectedIds(new Set());
            await load();
        } catch (err) {
            setInvalidateResult({ error: err?.response?.data?.detail || err.message });
        } finally {
            setInvalidating(false);
        }
    };

    const load = async () => {
        const params = { limit: LEDGER_PAGE_SIZE, offset: 0 };
        if (domain) params.domain = domain;
        if (q) params.q = q;
        if (since && since !== "all") params.since = since;
        const { data } = await api.get("/shed/ledger", { params });
        setEntries(data.entries || []);
        setCounts(data.counts || {});
        setTotalCountDocs(typeof data.total_count_documents === "number" ? data.total_count_documents : null);
        setHasMore(!!data.has_more);
        setNextOffset((data.offset || 0) + (data.limit || LEDGER_PAGE_SIZE));
        // Phase overlay — fire-and-forget; if it fails we still render the
        // ledger plainly. tz offset from browser so the bands read local.
        try {
            const ov = await api.get("/shed/overlay/ledger", {
                params: { tz_offset_hours: browserTzOffsetHours(), limit: 500, ...(domain ? { domain } : {}) },
            });
            const m = {};
            (ov.data?.entries || []).forEach((e) => {
                if (e?.id) m[e.id] = { lunar: e.lunar, circadian: e.circadian };
            });
            setOverlayMap(m);
            setOverlayCounts({
                by_lunar: ov.data?.by_lunar || {},
                by_circadian: ov.data?.by_circadian || {},
                total: ov.data?.total || 0,
            });
        } catch (_) {
            setOverlayMap({});
        }
    };

    const loadMore = React.useCallback(async () => {
        if (!hasMore || loadingMore) return;
        setLoadingMore(true);
        try {
            const params = { limit: LEDGER_PAGE_SIZE, offset: nextOffset, include_counts: false };
            if (domain) params.domain = domain;
            if (q) params.q = q;
            if (since && since !== "all") params.since = since;
            const { data } = await api.get("/shed/ledger", { params });
            setEntries((prev) => [...(prev || []), ...(data.entries || [])]);
            setHasMore(!!data.has_more);
            setNextOffset((data.offset || 0) + (data.limit || LEDGER_PAGE_SIZE));
            // Best-effort overlay merge for newly appended ids
            try {
                const ids = (data.entries || []).map((e) => e.id);
                if (ids.length) {
                    const ov = await api.get("/shed/overlay/ledger", {
                        params: { tz_offset_hours: browserTzOffsetHours(), limit: ids.length, ...(domain ? { domain } : {}) },
                    });
                    setOverlayMap((prev) => {
                        const m = { ...prev };
                        (ov.data?.entries || []).forEach((e) => {
                            if (e?.id) m[e.id] = { lunar: e.lunar, circadian: e.circadian };
                        });
                        return m;
                    });
                }
            } catch (_) { /* overlay is decorative */ }
        } finally {
            setLoadingMore(false);
        }
    }, [hasMore, loadingMore, nextOffset, domain, q, since]);
    useEffect(() => {
        const t = setTimeout(load, q ? 250 : 0);
        return () => clearTimeout(t);
    }, [domain, q, since]);

    const remove = async (id) => {
        if (!window.confirm("Redact this entry? (The file will remember it was here.)")) return;
        await api.delete(`/shed/entries/${id}`);
        load();
    };

    const reshred = async (id) => {
        setReshredError((s) => ({ ...s, [id]: "" }));
        setReshredding((s) => ({ ...s, [id]: true }));
        try {
            const { data } = await api.post(`/shed/entries/${id}/reshred`);
            if (data?.ok) {
                await load();
            } else {
                setReshredError((s) => ({
                    ...s,
                    [id]: data?.reason || "Classifier still empty — leave it and try again later.",
                }));
            }
        } catch (e) {
            setReshredError((s) => ({
                ...s,
                [id]: `[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`,
            }));
        } finally {
            setReshredding((s) => ({ ...s, [id]: false }));
        }
    };

    // Iter 31.19 (23 May 2026) — dramatic self-destruct countdown wraps
    // the actual wipe. User arms it; 10-second countdown begins; either
    // abort cancels it, or it fires automatically at zero. The actual
    // backend wipe is unchanged — still the same SSE clear.
    const armWipe = () => {
        setWipeError("");
        if (dangerConfirmText !== "WIPE") {
            setWipeError('Type WIPE exactly (capitals) to authorise.');
            return;
        }
        if (!exportManifest || exportManifest.total_rows > 0) {
            const proceed = window.confirm(
                "Last chance — have you downloaded your data?\n\n" +
                "Click OK to ARM the 10-second self-destruct sequence.\n" +
                "Click Cancel to grab the data export first."
            );
            if (!proceed) return;
        }
        setWipeCountdown(WIPE_COUNTDOWN_SECONDS);
    };

    const abortWipe = () => {
        setWipeCountdown(null);
        setWipeError("Self-destruct aborted. Ledger intact.");
    };

    // Tick the countdown every second; fire the wipe at zero.
    useEffect(() => {
        if (wipeCountdown === null) return undefined;
        if (wipeCountdown <= 0) {
            (async () => {
                setWipeCountdown(null);
                await _doWipe();
            })();
            return undefined;
        }
        const t = setTimeout(() => setWipeCountdown((s) => (s === null ? null : s - 1)), 1000);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wipeCountdown]);

    const _doWipe = async () => {
        setWipeError("");
        setWiping(true);
        try {
            // Iter 31.20 — expanded from ledger-only to full GDPR erase-all.
            // Wipes every user-scoped collection AND deletes the account row.
            // SSE-streamed like the old ledger clear so the CDN can't 504.
            const base = process.env.REACT_APP_BACKEND_URL || "";
            const token = getToken() || "";
            const resp = await fetch(`${base}/api/gdpr/erase-all`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ confirm: "ERASE", i_understand: true }),
            });
            if (!resp.ok) {
                const text = await resp.text().catch(() => "");
                throw new Error(`HTTP ${resp.status}: ${text.slice(0, 200) || resp.statusText}`);
            }
            let data = null;
            if (resp.body) {
                const reader = resp.body.getReader();
                const decoder = new TextDecoder();
                let buffer = "";
                while (true) {
                    const { value, done } = await reader.read();
                    if (done) break;
                    buffer += decoder.decode(value, { stream: true });
                    let idx;
                    while ((idx = buffer.indexOf("\n\n")) >= 0) {
                        const frame = buffer.slice(0, idx).trim();
                        buffer = buffer.slice(idx + 2);
                        if (!frame.startsWith("data:")) continue;
                        try {
                            const evt = JSON.parse(frame.slice(5).trim());
                            if (evt.type === "result") data = evt;
                            else if (evt.type === "error") throw new Error(evt.detail || "erase failed");
                        } catch (parseErr) {
                            if (parseErr.message?.includes("erase failed")) throw parseErr;
                        }
                    }
                }
            }
            if (!data) throw new Error("Stream ended without a result frame");
            setShowDanger(false);
            setDangerConfirmText("");
            window.alert(
                `Account erased.\n` +
                `• ${data.total_deleted?.toLocaleString() || 0} rows removed across ` +
                `${Object.keys(data.deleted || {}).length} collections.\n` +
                `• Account record deleted.\n\nSigning you out.`
            );
            try { localStorage.removeItem("msd_token"); localStorage.removeItem("msd_user"); } catch (_) { /* ignore */ }
            window.location.href = "/login?erased=1";
        } catch (e) {
            setWipeError(`${e.message || "erase failed"}`);
        } finally {
            setWiping(false);
        }
    };

    // Load export manifest whenever danger zone opens.
    const loadExportManifest = async () => {
        try {
            const { data } = await api.get("/data/export-manifest");
            setExportManifest(data);
        } catch (_) { /* non-fatal */ }
    };
    useEffect(() => {
        if (showDanger) loadExportManifest();
    }, [showDanger]);

    const downloadDataExport = async () => {
        setExporting(true);
        try {
            const base = process.env.REACT_APP_BACKEND_URL || "";
            const resp = await fetch(`${base}/api/data/export.zip`, {
                headers: { Authorization: `Bearer ${getToken() || ""}` },
            });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            const today = new Date().toISOString().slice(0, 10);
            a.href = url;
            a.download = `spyfinance-data-${today}.zip`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (e) {
            setWipeError(`Export failed: ${e.message}`);
        } finally {
            setExporting(false);
        }
    };

    const onImportFile = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = ""; // allow re-selecting same file
        if (!f) return;
        if (!window.confirm(
            `Import "${f.name}" into your account?\n\n` +
            "Existing rows are kept (idempotent — duplicates skipped by id).\n" +
            "Credentials are NOT restored; you'll need to reconnect Drive/Gmail."
        )) return;
        setImporting(true);
        setImportReport(null);
        setWipeError("");
        try {
            const base = process.env.REACT_APP_BACKEND_URL || "";
            const fd = new FormData();
            fd.append("file", f);
            const resp = await fetch(`${base}/api/data/import`, {
                method: "POST",
                headers: { Authorization: `Bearer ${getToken() || ""}` },
                body: fd,
            });
            const json = await resp.json();
            if (!resp.ok) throw new Error(json.detail || `HTTP ${resp.status}`);
            setImportReport(json);
            await load();
            await loadExportManifest();
        } catch (err) {
            setWipeError(`Import failed: ${err.message}`);
        } finally {
            setImporting(false);
        }
    };

    return (
        <Shell>
            <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-typed tw-prefix">Case File · Running Timeline</div>
                    <h1 className="font-heading text-5xl uppercase tracking-[0.04em] text-ink mt-1">
                        The Ledger<span className="cursor-blink" />
                    </h1>
                    <p className="font-body text-sm text-ink-soft mt-3">
                        Every filing, stacked by date. Finance, health, mood, everything.
                    </p>
                </div>
                <div className="pencil text-lg transform rotate-2">
                    "File now, question later." — No. 2
                </div>
            </div>

            <CleanupQueuePanel />

            <div className="paper-card p-4 mb-6 space-y-3" data-testid="ledger-filters">
                {/* Iter 31.45 — time-period pills. Default TODAY so a
                    60k-row ledger doesn't kill mobile on first paint.
                    All-time stays available but costs you a click,
                    not a free pageload. */}
                <div className="flex flex-wrap items-center gap-2">
                    <span className="label-typed tw-prefix pr-2">Window</span>
                    {[
                        { k: "today", label: "Today" },
                        { k: "week",  label: "Week" },
                        { k: "month", label: "Month" },
                        { k: "all",   label: "All time" },
                    ].map(({ k, label }) => {
                        const active = since === k;
                        return (
                            <button
                                key={k}
                                onClick={() => setSince(k)}
                                className={`font-heading uppercase text-[11px] tracking-[0.22em] px-3 py-2 border ${
                                    active
                                        ? "border-stamp-red text-stamp-red bg-paper-dark/50"
                                        : "border-ink/25 text-ink-soft hover:border-stamp-red hover:text-stamp-red"
                                }`}
                                data-testid={`filter-since-${k}`}
                            >
                                {label}
                            </button>
                        );
                    })}
                    {since !== "all" && totalCountDocs != null && (
                        <span
                            className="font-body text-[10px] text-ink-faint uppercase tracking-[0.18em] ml-2"
                            data-testid="filter-since-total-hint"
                            title="Lifetime ledger total (independent of the current window)"
                        >
                            · lifetime: {totalCountDocs.toLocaleString()}
                        </span>
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <button
                        onClick={() => setDomain("")}
                        className={`font-heading uppercase text-[11px] tracking-[0.22em] px-3 py-2 border ${
                            domain === "" ? "border-stamp-red text-stamp-red bg-paper-dark/50" : "border-ink/25 text-ink-soft hover:border-stamp-red hover:text-stamp-red"
                        }`}
                        data-testid="filter-all"
                        title={
                            totalCountDocs != null
                                ? `In-window total: ${Object.values(counts).reduce((a, b) => a + b, 0)} · Lifetime: ${totalCountDocs}`
                                : undefined
                        }
                    >
                        All domains · {Object.values(counts).reduce((a, b) => a + b, 0)}
                    </button>
                    <div className="flex-1 min-w-[200px] relative">
                        <Search className="w-3 h-3 text-ink-soft absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="search summaries / tags..."
                            className="w-full pl-9 pr-3 py-2 text-xs font-body"
                            data-testid="ledger-search"
                        />
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <span className="label-typed tw-prefix pr-2">Personal</span>
                    {LIFE_KEYS.map((k) => {
                        const m = DOMAIN_META[k];
                        const Icon = m.icon;
                        const n = counts[k] || 0;
                        const active = domain === k;
                        return (
                            <button
                                key={k}
                                onClick={() => setDomain(k)}
                                className={`font-heading uppercase text-[11px] tracking-[0.22em] px-3 py-2 border inline-flex items-center gap-2 ${active ? m.cls : "border-ink/25 text-ink-soft hover:border-ink hover:text-ink"}`}
                                data-testid={`filter-${k}`}
                            >
                                <Icon className="w-3 h-3" strokeWidth={1.5} />
                                {m.label} · {n}
                            </button>
                        );
                    })}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <span className="label-typed tw-prefix pr-2">Ops Desk</span>
                    {OPS_KEYS.map((k) => {
                        const m = DOMAIN_META[k];
                        const Icon = m.icon;
                        const n = counts[k] || 0;
                        const active = domain === k;
                        return (
                            <button
                                key={k}
                                onClick={() => setDomain(k)}
                                className={`font-heading uppercase text-[11px] tracking-[0.22em] px-3 py-2 border inline-flex items-center gap-2 ${active ? m.cls : "border-ink/25 text-ink-soft hover:border-ink hover:text-ink"}`}
                                data-testid={`filter-${k}`}
                            >
                                <Icon className="w-3 h-3" strokeWidth={1.5} />
                                {m.label} · {n}
                            </button>
                        );
                    })}
                </div>
            </div>

            {entries === null ? (
                <div className="flex items-center gap-2 text-ink-soft font-heading text-sm uppercase tracking-[0.3em]">
                    <Loader2 className="w-4 h-4 animate-spin" /> Opening the cabinet...
                </div>
            ) : entries.length === 0 ? (
                <div className="paper-card border border-dashed border-ink/30 p-16 text-center tilt-l" data-testid="ledger-empty">
                    <div className="font-heading text-3xl uppercase tracking-[0.06em] text-ink-faint">
                        Nothing filed
                    </div>
                    <div className="label-typed mt-3">
                        Post something through the Shredder and it'll land here.
                    </div>
                    {/* Iter 31.215 — agent-curated empty-state line. */}
                    <EasterEggSlot
                        path="ledger"
                        slot="ledger.empty_state"
                        as="div"
                        className="mt-4 text-[11px] font-body italic text-ink-soft opacity-70 max-w-md mx-auto"
                    />
                </div>
            ) : (
                <>
                {/* Phase overlay legend — circadian + lunar counts across this slice */}
                {overlayCounts.total > 0 && (
                    <div className="paper-card p-3 mb-4" data-testid="overlay-legend">
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 justify-between">
                            <div className="flex items-center gap-2">
                                <span className="label-typed">Phase overlay</span>
                                <button
                                    onClick={() => setShowOverlay((v) => !v)}
                                    className="font-heading uppercase text-[10px] tracking-[0.22em] border border-ink/30 px-2 py-1 hover:border-stamp-red hover:text-stamp-red"
                                    data-testid="overlay-toggle"
                                >
                                    {showOverlay ? "Hide strips" : "Show strips"}
                                </button>
                            </div>
                            <div className="flex flex-wrap items-center gap-2" data-testid="overlay-legend-circadian">
                                <span className="label-typed pr-1">Time of day</span>
                                {Object.entries(overlayCounts.by_circadian || {})
                                    .sort((a, b) => b[1] - a[1])
                                    .map(([k, n]) => {
                                        const m = circadianMeta(k);
                                        return (
                                            <span
                                                key={k}
                                                title={`${m.label} · ${n} entries`}
                                                className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-heading uppercase tracking-[0.18em] ${m.cls}`}
                                                data-testid={`legend-circadian-${k}`}
                                            >
                                                {m.label} · {n}
                                            </span>
                                        );
                                    })}
                            </div>
                            <div className="flex flex-wrap items-center gap-2" data-testid="overlay-legend-lunar">
                                <span className="label-typed pr-1">Moon</span>
                                {Object.entries(overlayCounts.by_lunar || {})
                                    .sort((a, b) => b[1] - a[1])
                                    .map(([k, n]) => {
                                        const m = lunarMeta(k);
                                        return (
                                            <span
                                                key={k}
                                                title={`${m.label} · ${n} entries`}
                                                className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-heading uppercase tracking-[0.18em] ${m.cls}`}
                                                data-testid={`legend-lunar-${k}`}
                                            >
                                                <span aria-hidden>{m.glyph}</span> {m.label} · {n}
                                            </span>
                                        );
                                    })}
                            </div>
                        </div>
                    </div>
                )}
                <ol className="relative border-l-2 border-ink/25 ml-4 pl-8" data-testid="ledger-timeline">
                    {/* Iter 31.205 — bulk-invalidate action bar. */}
                    <li className="-ml-12 mb-3 list-none flex flex-wrap items-center gap-2 text-xs font-heading uppercase tracking-[0.18em]" data-testid="ledger-bulk-bar">
                        <span className="text-ink-soft">Bulk:</span>
                        <button
                            type="button"
                            onClick={selectAllVisible}
                            className="border border-ink/30 px-2 py-1 hover:border-stamp-red hover:text-stamp-red"
                            data-testid="ledger-bulk-select-all"
                        >Select page</button>
                        <button
                            type="button"
                            onClick={clearSelection}
                            disabled={selectedIds.size === 0}
                            className="border border-ink/30 px-2 py-1 disabled:opacity-30 hover:border-stamp-red hover:text-stamp-red"
                            data-testid="ledger-bulk-clear"
                        >Clear</button>
                        <button
                            type="button"
                            onClick={openInvalidateSelected}
                            disabled={selectedIds.size === 0}
                            className="border border-stamp-red px-2 py-1 text-stamp-red disabled:opacity-30 hover:bg-stamp-red hover:text-paper-light"
                            data-testid="ledger-bulk-invalidate-selected"
                        >Invalidate {selectedIds.size} selected</button>
                        <button
                            type="button"
                            onClick={openInvalidateByFilter}
                            className="border border-stamp-red px-2 py-1 text-stamp-red hover:bg-stamp-red hover:text-paper-light"
                            data-testid="ledger-bulk-invalidate-filter"
                            title="Invalidate every row matching the current filter set (preview before commit)"
                        >Invalidate ALL matching filter</button>
                        <span className="text-ink-soft text-[10px] normal-case tracking-normal">
                            Canon-safe: files invalidation_correction rows; originals stay in the chain.
                        </span>
                    </li>
                    {entries.map((e) => {
                        const m = DOMAIN_META[e.domain] || DOMAIN_META.other;
                        const Icon = m.icon;
                        const isOpen = expanded[e.id];
                        const ov = overlayMap[e.id];
                        const lm = ov?.lunar?.label ? lunarMeta(ov.lunar.label) : null;
                        const cm = ov?.circadian?.label ? circadianMeta(ov.circadian.label) : null;
                        return (
                            <li key={e.id} className="mb-6 ml-6" data-testid={`ledger-entry-${e.id}`}>
                                <span
                                    className={`absolute -left-[11px] flex items-center justify-center w-[22px] h-[22px] border-2 bg-paper-light ${m.cls}`}
                                >
                                    <Icon className="w-3 h-3" strokeWidth={2} />
                                </span>
                                {/* Iter 31.205 — selectable checkbox for bulk invalidate. */}
                                <label
                                    className="absolute -left-[44px] top-0 cursor-pointer p-1"
                                    title="Select for bulk invalidate"
                                    data-testid={`ledger-select-${e.id}-wrap`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.has(e.id)}
                                        onChange={() => toggleSelected(e.id)}
                                        className="w-4 h-4 accent-stamp-red cursor-pointer"
                                        data-testid={`ledger-select-${e.id}`}
                                    />
                                </label>
                                <div className="paper-card p-4 relative overflow-hidden">
                                    {/* Phase strip — thin tinted bar along the left of the card,
                                        plus two pills inside. Half circadian, half lunar shading.
                                        Hidden when overlay toggled off. */}
                                    {showOverlay && (cm || lm) && (
                                        <div
                                            className="absolute left-0 top-0 bottom-0 w-1.5 flex flex-col"
                                            aria-hidden
                                            data-testid={`entry-phase-strip-${e.id}`}
                                        >
                                            {cm && <div className={`flex-1 ${cm.dot}`} />}
                                            {lm && <div className={`flex-1 ${lm.dot}`} />}
                                        </div>
                                    )}
                                    <div className={showOverlay && (cm || lm) ? "pl-3" : ""}>
                                    <div className="flex items-start justify-between gap-3 mb-2">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center flex-wrap gap-x-3 gap-y-1 label-typed mb-1">
                                                <span className={`px-1.5 py-0.5 border ${m.cls}`}>{m.label}</span>
                                                <span>{e.timestamp?.replace("T", " ").slice(0, 16)}</span>
                                                {e.source_file && <span>· {e.source_file}</span>}
                                                {e.confidence != null && (
                                                    <span>· confidence {Math.round(e.confidence * 100)}%</span>
                                                )}
                                                {showOverlay && cm && (
                                                    <span
                                                        title={`Logged ${cm.label.toLowerCase()} (${ov.circadian.hour_local}:00 local)`}
                                                        className={`inline-flex items-center px-1.5 py-0.5 text-[9px] tracking-[0.18em] uppercase ${cm.cls}`}
                                                        data-testid={`entry-circadian-${e.id}`}
                                                    >
                                                        {cm.label}
                                                    </span>
                                                )}
                                                {showOverlay && lm && (
                                                    <span
                                                        title={`Moon: ${lm.label} (${Math.round((ov.lunar.illumination || 0) * 100)}% illuminated)`}
                                                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] tracking-[0.18em] uppercase ${lm.cls}`}
                                                        data-testid={`entry-lunar-${e.id}`}
                                                    >
                                                        <span aria-hidden>{lm.glyph}</span> {lm.label}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="font-body text-sm text-ink leading-relaxed">{e.summary}</div>
                                            {e.tags?.length > 0 && (
                                                <div className="flex flex-wrap gap-1 mt-2">
                                                    {e.tags.map((t) => (
                                                        <span key={t} className="text-[10px] font-body uppercase tracking-[0.15em] text-ink-soft border border-ink/20 px-1.5 py-0.5">
                                                            {t}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                            {/* iter 31.35 — routing-rule audit pills.
                                                `alerts[]` populated by threshold_alert actions;
                                                `routing_rules_fired[]` is the per-entry audit of
                                                every approved rule that matched at filing time. */}
                                            {(e.alerts?.length > 0 || e.routing_rules_fired?.length > 0) && (
                                                <div className="flex flex-wrap gap-1 mt-2" data-testid={`entry-rules-${e.id}`}>
                                                    {(e.alerts || []).map((a, i) => (
                                                        <span
                                                            key={`a-${i}`}
                                                            title={a.natural_summary || `${a.kind} alert · threshold ${a.threshold}, value ${a.amount}`}
                                                            className="text-[10px] font-heading uppercase tracking-[0.15em] text-amber-400 border border-amber-400/60 bg-amber-400/10 px-1.5 py-0.5 inline-flex items-center gap-1"
                                                            data-testid={`entry-alert-${e.id}-${i}`}
                                                        >
                                                            <AlertTriangle className="w-3 h-3" />
                                                            {a.kind === "threshold"
                                                                ? `${a.amount} > ${a.threshold}`
                                                                : a.kind || "alert"}
                                                        </span>
                                                    ))}
                                                    {(e.routing_rules_fired || []).map((rf, i) => (
                                                        <span
                                                            key={`r-${i}`}
                                                            title={`Rule fired: ${rf.action}${rf.applied ? ` · ${JSON.stringify(rf.applied)}` : ""}`}
                                                            className="text-[10px] font-heading uppercase tracking-[0.15em] text-stamp-red border border-stamp-red/60 bg-stamp-red/5 px-1.5 py-0.5 inline-flex items-center gap-1"
                                                            data-testid={`entry-rule-fired-${e.id}-${i}`}
                                                        >
                                                            Rule · {(rf.action || "").replace(/_/g, " ")}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {(e.structured?.unclassified === true || (e.tags || []).includes("unclassified")) && e.structured?.raw_text && (
                                                <button
                                                    onClick={() => reshred(e.id)}
                                                    disabled={!!reshredding[e.id]}
                                                    className="font-heading text-[11px] uppercase tracking-[0.22em] inline-flex items-center gap-1 border border-stamp-red text-stamp-red px-2 py-1 hover:bg-stamp-red hover:text-paper disabled:opacity-40 disabled:cursor-not-allowed"
                                                    data-testid={`entry-reshred-${e.id}`}
                                                    title="Re-run the classifier on this entry"
                                                >
                                                    <RefreshCcw className={`w-3 h-3 ${reshredding[e.id] ? "animate-spin" : ""}`} />
                                                    {reshredding[e.id] ? "Re-shredding…" : "Re-shred"}
                                                </button>
                                            )}
                                            {Object.keys(e.structured || {}).length > 0 && (
                                                <button
                                                    onClick={() => setExpanded({ ...expanded, [e.id]: !isOpen })}
                                                    className="font-heading text-[11px] uppercase tracking-[0.22em] text-ink-soft hover:text-stamp-red"
                                                    data-testid={`entry-expand-${e.id}`}
                                                >
                                                    {isOpen ? "− data" : "+ data"}
                                                </button>
                                            )}
                                            <button
                                                onClick={() => remove(e.id)}
                                                className="text-ink-soft hover:text-stamp-red"
                                                data-testid={`entry-delete-${e.id}`}
                                                title="Redact entry"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                    {isOpen && (
                                        <pre className="mt-3 border border-ink/15 bg-paper-dark/30 p-3 text-[11px] font-body text-ink-soft overflow-x-auto" data-testid={`entry-json-${e.id}`}>
                                            {JSON.stringify(e.structured, null, 2)}
                                        </pre>
                                    )}
                                    {reshredError[e.id] && (
                                        <div
                                            className="mt-2 font-body text-xs text-stamp-red border border-stamp-red/40 px-2 py-1"
                                            data-testid={`entry-reshred-error-${e.id}`}
                                        >
                                            {reshredError[e.id]}
                                        </div>
                                    )}
                                    </div> {/* /pl-3 wrapper */}
                                </div>
                            </li>
                        );
                    })}
                </ol>
                {hasMore && (
                    <LedgerLoadMore onVisible={loadMore} busy={loadingMore} />
                )}
                {!hasMore && entries.length > 0 && (
                    <div
                        className="mt-6 mb-2 label-typed text-[10px] tracking-[0.22em] text-ink-faint text-center"
                        data-testid="ledger-end-of-list"
                    >
                        End of the file · {entries.length} entries loaded
                    </div>
                )}
                </>
            )}

            {/* Danger Zone — operator-initiated full Ledger reset.
                Canon Rule 1 protects against silent edits; deliberate operator
                reset is a different operation. Triple-gated by design. */}
            <div className="mt-12 hairline" />
            <div className="mt-6 paper-card border-2 border-stamp-red/40 p-4" data-testid="ledger-danger-zone">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <div className="label-typed text-stamp-red flex items-center gap-2">
                            <AlertTriangle className="w-3 h-3" />
                            Danger zone · full account erase (GDPR)
                        </div>
                        <div className="font-body text-xs text-ink-soft mt-1 max-w-md">
                            Wipes every collection holding your user_id — Ledger, CV, Comms, Spy Finance, Vault, Radio, integrations, payment history — <em>and</em> deletes your account record. Cannot be undone. See{" "}
                            <a href="/gdpr" className="underline text-stamp-red">/gdpr</a> for the full inventory and policy.
                        </div>
                    </div>
                    <button
                        onClick={() => setShowDanger((s) => !s)}
                        data-testid="ledger-danger-toggle"
                        className="font-heading uppercase text-[10px] tracking-[0.22em] border border-stamp-red text-stamp-red px-3 py-2 hover:bg-stamp-red hover:text-paper"
                    >
                        {showDanger ? "Stand down" : "Begin reset"}
                    </button>
                </div>
                {showDanger && (
                    <div className="mt-4 border-t border-stamp-red/30 pt-4 space-y-4">
                        {/* GDPR-friendly download-first prompt — keep this above the wipe controls
                            so the operator can't fat-finger destruction without seeing it. */}
                        <div className="border border-ink/20 p-3">
                            <div className="label-typed text-[10px] tracking-[0.22em] text-stamp-red mb-1">
                                Download your data first
                            </div>
                            <p className="font-body text-[12px] text-ink-soft mb-3 leading-relaxed">
                                {exportManifest
                                    ? `Your account holds ${exportManifest.total_rows} rows across ${Object.keys(exportManifest.counts || {}).length} collections. Grab the JSON archive before any wipe — GDPR Article 20.`
                                    : "Calculating your data footprint…"}
                            </p>
                            <button
                                onClick={downloadDataExport}
                                disabled={exporting}
                                data-testid="ledger-data-export-btn"
                                className="inline-flex items-center gap-2 font-heading uppercase text-[10px] tracking-[0.22em] border border-ink/30 text-ink px-3 py-2 hover:border-stamp-red hover:text-stamp-red disabled:opacity-50"
                            >
                                {exporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                                {exporting ? "Bundling…" : "Download my data (ZIP)"}
                            </button>
                            <button
                                onClick={() => importInputRef.current?.click()}
                                disabled={importing}
                                data-testid="ledger-data-import-btn"
                                className="ml-2 inline-flex items-center gap-2 font-heading uppercase text-[10px] tracking-[0.22em] border border-ink/30 text-ink px-3 py-2 hover:border-stamp-red hover:text-stamp-red disabled:opacity-50"
                                title="Re-import a previous export. Idempotent — duplicates skipped by id."
                            >
                                {importing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                                {importing ? "Importing…" : "Import a previous export"}
                            </button>
                            <input
                                ref={importInputRef}
                                type="file"
                                accept=".zip,application/zip"
                                onChange={onImportFile}
                                className="hidden"
                                data-testid="ledger-data-import-input"
                            />
                            {importReport && (
                                <div className="mt-3 border border-stamp-green/40 bg-stamp-green/5 p-3 font-body text-[11px] text-ink" data-testid="ledger-import-report">
                                    <div className="label-typed text-[10px] tracking-[0.22em] text-stamp-green mb-1">
                                        Import complete
                                    </div>
                                    <div>Inserted: <strong>{importReport.totals?.inserted ?? 0}</strong> · Skipped duplicates: <strong>{importReport.totals?.skipped_duplicates ?? 0}</strong> · Errors: <strong>{importReport.totals?.errors ?? 0}</strong></div>
                                    <div className="text-ink-soft text-[10px] mt-1">
                                        Drive/Gmail credentials are not restored — reconnect them from the respective Watch pages.
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Wipe controls — disabled while a countdown is armed.  */}
                        <div>
                            <div className="font-body text-xs text-ink mb-2">
                                Type <span className="font-heading text-stamp-red tracking-[0.15em]">WIPE</span> below to authorise. Canon Rule 1 protects against silent edits — this is your deliberate reset.
                            </div>
                            <div className="flex gap-2 flex-wrap">
                                <input
                                    value={dangerConfirmText}
                                    onChange={(e) => setDangerConfirmText(e.target.value)}
                                    placeholder="WIPE"
                                    data-testid="ledger-danger-confirm-input"
                                    disabled={wipeCountdown !== null || wiping}
                                    className="bg-paper-light border border-ink/30 px-3 py-2 font-body text-sm text-ink focus:outline-none focus:border-stamp-red w-32 tracking-[0.15em] disabled:opacity-50"
                                />
                                <button
                                    onClick={armWipe}
                                    disabled={wiping || wipeCountdown !== null || dangerConfirmText !== "WIPE"}
                                    data-testid="ledger-danger-confirm-btn"
                                    className="font-heading uppercase text-[10px] tracking-[0.22em] bg-stamp-red text-paper px-4 py-2 disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                    {wiping ? "Wiping…" : "Arm self-destruct"}
                                </button>
                            </div>
                            {wipeError && (
                                <div className="font-body text-xs text-stamp-red mt-2" data-testid="ledger-danger-error">
                                    {wipeError}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Dramatic self-destruct countdown overlay.
                    Fires the wipe automatically at zero; abort to cancel. */}
                {wipeCountdown !== null && (
                    <div
                        className="fixed inset-0 z-[2147483646] bg-black/85 backdrop-blur-sm flex items-center justify-center p-6"
                        data-testid="wipe-countdown-overlay"
                        role="alertdialog"
                        aria-live="assertive"
                    >
                        <div className="paper-card p-8 max-w-md w-full border-2 border-stamp-red shadow-2xl text-center">
                            <div className="label-typed text-stamp-red text-[10px] tracking-[0.3em] mb-2">
                                Self-destruct sequence
                            </div>
                            <div className="font-heading text-2xl text-ink mb-6">
                                Ledger wipe armed
                            </div>
                            <div
                                className="font-heading text-stamp-red mb-6 animate-pulse"
                                style={{ fontSize: "clamp(72px, 18vw, 144px)", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}
                                data-testid="wipe-countdown-number"
                            >
                                {wipeCountdown}
                            </div>
                            <p className="font-body text-sm text-ink-soft mb-6 leading-relaxed">
                                The wipe fires automatically at zero. Every life-entry, every CV selection, every LinkedIn key — gone. Originals never altered until then (Rule 1). This is the last clean exit.
                            </p>
                            <button
                                onClick={abortWipe}
                                data-testid="wipe-abort-btn"
                                className="inline-flex items-center gap-2 px-6 py-3 bg-ink text-paper-light font-heading uppercase tracking-[0.22em] text-xs hover:bg-stamp-red transition-colors"
                            >
                                Abort
                            </button>
                        </div>
                    </div>
                )}
                {/* Iter 31.205 — canon-safe bulk invalidate modal. */}
                {showInvalidateModal && (
                    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" data-testid="ledger-invalidate-modal">
                        <div className="bg-[rgb(var(--c-paper))] border-0 max-w-lg w-full p-6 paper-card">
                            <div className="label-typed text-stamp-red mb-2">
                                {invalidateMode === "selected"
                                    ? `Invalidate ${selectedIds.size} selected`
                                    : "Invalidate ALL matching filter"}
                            </div>
                            <p className="font-body text-sm text-ink-soft leading-relaxed mb-4">
                                Files an <code className="text-stamp-red">invalidation_correction</code> per row.
                                Originals stay in the hash chain (Rule 1). Reads hide them by default;
                                <code> ?include_invalidated=true </code> brings them back for audit.
                                Reversible by filing a counter-correction.
                            </p>
                            {invalidateMode === "filter" && (
                                <div className="border border-ink/30 bg-paper-light p-3 mb-4 text-sm font-body text-ink">
                                    {filterMatchCount === null && <span>Counting matches…</span>}
                                    {filterMatchCount === -1 && (
                                        <span className="text-stamp-red">
                                            Filter preview failed. Tighten filters and try again.
                                        </span>
                                    )}
                                    {typeof filterMatchCount === "number" && filterMatchCount >= 0 && (
                                        <span>
                                            Matches: <strong>{filterMatchCount}</strong> row(s)
                                            <span className="text-ink-soft text-xs ml-2">
                                                (domain={domain || "all"} · q={q || "—"} · since={since || "all"})
                                            </span>
                                        </span>
                                    )}
                                </div>
                            )}
                            <label className="block label-typed text-[10px] tracking-[0.22em] text-ink-soft mb-1">
                                Reason (required, becomes the correction's body text)
                            </label>
                            <textarea
                                value={invalidateReason}
                                onChange={(e) => setInvalidateReason(e.target.value)}
                                placeholder="e.g. low-res CV scans, re-uploading at proper resolution"
                                className="w-full border border-ink/30 bg-paper-light px-3 py-2 font-body text-sm text-ink resize-none mb-4"
                                rows={3}
                                data-testid="invalidate-reason-input"
                            />
                            {invalidateResult && !invalidateResult.error && (
                                <div className="border border-stamp-green/50 bg-stamp-green/5 p-3 mb-4 text-sm font-body text-ink" data-testid="invalidate-result-success">
                                    Filed <strong>{invalidateResult.invalidated || invalidateResult.would_invalidate || 0}</strong> invalidation correction(s).
                                    {invalidateResult.skipped > 0 && (
                                        <span className="text-ink-soft"> {invalidateResult.skipped} already invalidated, skipped.</span>
                                    )}
                                </div>
                            )}
                            {invalidateResult?.error && (
                                <div className="border border-stamp-red/50 bg-stamp-red/5 p-3 mb-4 text-sm font-body text-stamp-red" data-testid="invalidate-result-error">
                                    {invalidateResult.error}
                                </div>
                            )}
                            <div className="flex items-center gap-2 justify-end">
                                <button
                                    onClick={() => setShowInvalidateModal(false)}
                                    className="border border-ink/30 px-3 py-2 font-heading uppercase tracking-[0.22em] text-xs text-ink hover:border-stamp-red hover:text-stamp-red"
                                    data-testid="invalidate-cancel-btn"
                                >Close</button>
                                <button
                                    onClick={executeInvalidate}
                                    disabled={
                                        invalidating ||
                                        invalidateReason.trim().length < 2 ||
                                        (invalidateMode === "filter" && (filterMatchCount === null || filterMatchCount === -1 || filterMatchCount === 0)) ||
                                        (invalidateMode === "selected" && selectedIds.size === 0)
                                    }
                                    className="px-3 py-2 bg-stamp-red text-paper-light font-heading uppercase tracking-[0.22em] text-xs hover:bg-ink disabled:opacity-30"
                                    data-testid="invalidate-confirm-btn"
                                >
                                    {invalidating ? "Filing…" : "File invalidation"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Shell>
    );
}


/**
 * LedgerLoadMore — IntersectionObserver-driven sentinel sitting at the
 * end of the timeline. Fires `onVisible` once whenever it scrolls into
 * the viewport so the parent can fetch the next page (iter 31.31).
 */
function LedgerLoadMore({ onVisible, busy }) {
    const ref = React.useRef(null);
    React.useEffect(() => {
        const node = ref.current;
        if (!node) return undefined;
        const io = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) onVisible?.();
                }
            },
            { rootMargin: "200px 0px" },
        );
        io.observe(node);
        return () => io.disconnect();
    }, [onVisible]);
    return (
        <div
            ref={ref}
            className="mt-6 mb-4 py-4 flex items-center justify-center label-typed text-[10px] tracking-[0.22em] text-ink-soft"
            data-testid="ledger-load-more-sentinel"
        >
            {busy ? (
                <>
                    <Loader2 className="w-3 h-3 animate-spin mr-2" /> Pulling the next page…
                </>
            ) : (
                <span>Scroll for more</span>
            )}
        </div>
    );
}
